import { Step } from 'react-joyride';

// Business Tour Steps
export const getBusinessTourSteps = (): Step[] => [
  // 1. Welcome - Dashboard (Centered Generic)
  {
    target: 'body',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold text-lg">Welcome to the Tutorial!</h3>
        <p className="text-sm">
          Let's take a quick tour to get you familiar with your new business dashboard and tools.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  // 1.5. Sidebar - Dashboard (New)
  {
    target: '[data-tour="header-sidebar"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Navigation Sidebar</h3>
        <p className="text-sm">
          Use the sidebar to navigate between your dashboard, calendar, chats, and settings.
          Hover over icons to see their labels.
        </p>
      </div>
    ),
    placement: 'right',
  },
  // 2. Upcoming Appointments - Dashboard
  {
    target: '[data-tour="business-upcoming-appointments"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Upcoming Appointments</h3>
        <p className="text-sm">
          Keep track of your schedule with this list of upcoming appointments.
          Never miss a meeting with your clients.
        </p>
      </div>
    ),
    placement: 'left',
  },
  // 3. Recent Conversations - Dashboard
  {
    target: '[data-tour="business-recent-conversations"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Recent Conversations</h3>
        <p className="text-sm">
          Quickly access your most recent chats with clients. Click on any conversation
          to jump straight into the message view.
        </p>
      </div>
    ),
    placement: 'top',
  },
  // 4. Calendar Views - Appointments
  {
    target: '[data-tour="business-calendar-views"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Calendar Views</h3>
        <p className="text-sm">
          Switch between Daily, Weekly, and Monthly views to see your schedule
          in the way that works best for you.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // 5. Calendar Grid - Appointments
  {
    target: '[data-tour="business-calendar-grid"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Calendar Grid</h3>
        <p className="text-sm">
          Click on any time slot to quickly schedule a new appointment or mark
          yourself as unavailable for that time.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // 6. Manage Availability - Appointments
  {
    target: '[data-tour="business-calendar-tab-unavailability"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Manage Availability</h3>
        <p className="text-sm">
          Switch to the Unavailability tab to block out times when you are not available.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  // 8. Chat List - Chat
  {
    target: '[data-tour="business-chat-list"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Conversation List</h3>
        <p className="text-sm">
          This panel shows all your active conversations. Use the search bar to find
          specific clients or browse through your history.
        </p>
      </div>
    ),
    placement: 'right',
  },
  // 7. Chat Area - Chat
  {
    target: '[data-tour="business-chat-area"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Messaging Area</h3>
        <p className="text-sm">
          Send text messages to your clients here. You can also view the full
          conversation history and see read receipts.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // 8. Contact Search - Contacts
  {
    target: '[data-tour="business-contacts-search"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Contact Search</h3>
        <p className="text-sm">
          Easily find specific clients from your contact list. You can search by name,
          phone number, or other details.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // 9. Column Settings - Contacts
  {
    target: '[data-tour="business-contacts-settings"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Column Settings</h3>
        <p className="text-sm">
          Customize your view by showing or hiding table columns to see exactly the information you need.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // 10. Add Client - Contacts
  {
    target: '[data-tour="business-contacts-add"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Add New Client</h3>
        <p className="text-sm">
          Manually add a new client to your contacts list.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // 11. Import Clients - Contacts
  {
    target: '[data-tour="business-contacts-import"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Import Clients</h3>
        <p className="text-sm">
          Bulk import your existing client list using CSV or Excel files.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // 12. Export Clients - Contacts
  {
    target: '[data-tour="business-contacts-export"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Export Clients</h3>
        <p className="text-sm">
          Download your client list for backup or external use.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // 15. AI General Settings - Chatbot Settings
  {
    target: '[data-tour="business-chatbot-tab-general"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">AI General Settings</h3>
        <p className="text-sm">
          Enable or disable your chatbot and configure basic behavior settings from this tab.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // 16. AI Prompts - Chatbot Settings
  {
    target: '[data-tour="business-chatbot-tab-prompts"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">AI Prompts</h3>
        <p className="text-sm">
          Customize the exact messages your AI assistant uses for welcomes, reminders, and follow-ups.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // 17. AI Business Hours - Chatbot Settings
  {
    target: '[data-tour="business-chatbot-tab-hours"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Business Hours</h3>
        <p className="text-sm">
          Define your operating hours so the AI knows when to step in and handle after-hours inquiries.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // 13. Profile - Back to Profile
  {
    target: '[data-tour="profile-settings"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Profile & Settings</h3>
        <p className="text-sm">
          You can always come back here to update your profile, change settings, or restart this tour.
        </p>
      </div>
    ),
    placement: 'center',
  },
];

// Manager Tour Steps
export const getManagerTourSteps = (): Step[] => [
  // Manager: Welcome step
  {
    target: '[data-tour="dashboard-welcome"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold text-lg">Welcome to Synkro!</h3>
        <p className="text-sm">
          Let's take a quick tour to get you familiar with your manager dashboard.
          This is where you'll oversee all your projects and team activities.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },

  {
    target: '[data-tour="header-sidebar"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Navigation Sidebar</h3>
        <p className="text-sm">
          This is your main navigation sidebar. Use it to quickly access your dashboard,
          projects, calendar, research tools, and clients. Hover over it to see full labels.
        </p>
      </div>
    ),
    placement: 'right',
  },

  {
    target: '[data-tour="header-project-selector"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Project Selector</h3>
        <p className="text-sm">
          Switch between your active projects here. Click to see all available projects
          and select the one you want to work on. The current project is highlighted.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="header-notifications"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Notifications</h3>
        <p className="text-sm">
          Stay updated with project activities, mentions, deadlines, and important updates.
          The badge shows unread notifications. Click to view and manage all notifications.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="header-messages"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Messages</h3>
        <p className="text-sm">
          Communicate with your team members and clients. View your recent conversations
          and direct messages. The badge indicates unread messages.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="header-notes-icon"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Notes</h3>
        <p className="text-sm">
          Access your personal notes and knowledge base. Click here to view, create, and organize
          your notes. Keep important information at your fingertips.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="header-pomodoro-timer"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Pomodoro Timer</h3>
        <p className="text-sm">
          Use the Pomodoro technique to manage your work time. Click to start a focused work session,
          take breaks, and boost your productivity. The timer helps you maintain focus and rest cycles.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="header-three-dots-menu"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Quick Actions Menu</h3>
        <p className="text-sm">
          Access quick actions like creating events, tasks, and notes. You can also open
          the AI Assistant from here. This menu provides shortcuts to common actions.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="header-avatar"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Your Profile</h3>
        <p className="text-sm">
          Click on your avatar to access your profile settings, theme preferences, contacts,
          usage statistics, and account options. Manage your account and preferences here.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="projects-section"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Projects Overview</h3>
        <p className="text-sm">
          Here you can see all your active projects. Click on any project to view details,
          manage team members, and track progress.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="upcoming-items"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Upcoming Deadlines</h3>
        <p className="text-sm">
          Stay on top of important deadlines and milestones. Never miss a critical
          deliverable with proactive deadline tracking.
        </p>
      </div>
    ),
    placement: 'left',
  },

  {
    target: '[data-tour="project-tasks"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Task Management</h3>
        <p className="text-sm">
          Oversee task completion across all projects. Monitor progress, assign
          work, and ensure nothing falls through the cracks.
        </p>
      </div>
    ),
    placement: 'left',
  },

  {
    target: '[data-tour="recent-activity"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Recent Activity</h3>
        <p className="text-sm">
          Keep track of the latest updates and changes across your projects.
          Stay informed about team progress and project developments.
        </p>
      </div>
    ),
    placement: 'left',
  },

  {
    target: '[data-tour="project-team"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Team Management</h3>
        <p className="text-sm">
          Manage your team members, track their performance, and ensure optimal
          resource allocation across projects.
        </p>
      </div>
    ),
    placement: 'right',
  },

  // Manager: Project statistics
  {
    target: '[data-tour="assigned-clients"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Project Stats</h3>
        <p className="text-sm">
          Monitor key project metrics and performance indicators.
          Get insights into project health and completion rates.
        </p>
      </div>
    ),
    placement: 'right',
  },

  {
    target: '[data-tour="create-project"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Create New Projects</h3>
        <p className="text-sm">
          Start new projects here. You can define objectives, assign team members,
          and set up project templates.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="create-project-button"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Start New Projects</h3>
        <p className="text-sm">
          Click this button to create a new project.
          Choose from templates or create custom workflows.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  // Manager: Available consultants for assignment
  {
    target: '[data-tour="available-consultants-section"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Available Consultants</h3>
        <p className="text-sm">
          This panel shows all consultants available for assignment. Drag consultants from here
          onto project cards to assign them to specific projects. The number shows how many projects
          they're currently assigned to.
        </p>
      </div>
    ),
    placement: 'left',
  },

  // Manager: Available clients for assignment
  {
    target: '[data-tour="available-clients-section"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Available Clients</h3>
        <p className="text-sm">
          This panel displays clients that can be assigned to projects. Drag clients from here
          onto project cards to assign them. The number indicates how many active projects they're
          currently assigned to.
        </p>
      </div>
    ),
    placement: 'left',
  },

  {
    target: '[data-tour="calendar-section"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Calendar & Scheduling</h3>
        <p className="text-sm">
          Manage deadlines, schedule meetings, and track important dates
          across all your projects.
        </p>
      </div>
    ),
    placement: 'center',
  },

  //
  // RESEARCH TOOLS — Deduplicated
  //

  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Conversational AI Assistant</h3>
        <p className="text-sm">
          Your AI-powered research assistant that can analyze documents,
          answer questions, and provide insights about your projects.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="ai-chat-menu-toggle"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Chat History Menu</h3>
        <p className="text-sm">
          Toggle the chat history sidebar to view and manage all your conversations.
          Access previous chats and start new ones.
        </p>
      </div>
    ),
    placement: 'right',
  },

  {
    target: '[data-tour="ai-chat-new-session"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Start New Chat</h3>
        <p className="text-sm">
          Create a new conversation session. Each chat maintains its own context
          and history for focused discussions.
        </p>
      </div>
    ),
    placement: 'right',
  },

  {
    target: '[data-tour="ai-chat-file-panel"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Research Files</h3>
        <p className="text-sm">
          Manage uploaded files and documents. The AI can analyze these files
          and use them as context for more accurate responses.
        </p>
      </div>
    ),
    placement: 'left',
  },

  {
    target: '[data-tour="ai-chat-message-input"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Message Input</h3>
        <p className="text-sm">
          Type your questions and requests here. Press Enter to send, or Shift+Enter for new lines.
          The AI will respond based on your uploaded files and conversation history.
        </p>
      </div>
    ),
    placement: 'top',
  },

  {
    target: '[data-tour="ai-chat-attach-files"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Attach Files</h3>
        <p className="text-sm">
          Upload documents, PDFs, images, and spreadsheets to provide context
          for your AI conversations.
        </p>
      </div>
    ),
    placement: 'top',
  },

  {
    target: '[data-tour="ai-chat-voice-input"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Voice Input</h3>
        <p className="text-sm">
          Use voice input to communicate with the AI assistant. Click to start recording
          and speak your questions or requests.
        </p>
      </div>
    ),
    placement: 'top',
  },

  {
    target: '[data-tour="ai-chat-send-message"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Send Message</h3>
        <p className="text-sm">
          Send your message to the AI assistant. The send button turns red during
          streaming responses—click it to stop generation if needed.
        </p>
      </div>
    ),
    placement: 'top',
  },

  //
  // VIDEO INSIGHTS — Deduplicated
  //

  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Video Insights</h3>
        <p className="text-sm">
          Extract key information from videos and multimedia content.
          Upload videos or provide URLs to get AI-powered analysis.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="video-browse"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Browse & Upload Videos</h3>
        <p className="text-sm">
          Upload videos from your device or import from project files.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="video-saved-insights"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Saved Video Insights</h3>
        <p className="text-sm">
          Access all your previously analyzed videos. View transcripts,
          summaries, and generated content.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="video-url-input"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Video URL Input</h3>
        <p className="text-sm">
          Paste URLs from YouTube or other platforms to analyze remote videos.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="video-transcript-tab"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Video Transcript</h3>
        <p className="text-sm">
          View the full transcript. Click on timestamps to jump to exact moments.
        </p>
      </div>
    ),
    placement: 'top',
  },

  {
    target: '[data-tour="video-minutes-tab"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Meeting Minutes</h3>
        <p className="text-sm">
          Automatically generated minutes and key discussion points.
        </p>
      </div>
    ),
    placement: 'top',
  },

  {
    target: '[data-tour="video-summary-tab"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">AI Summary</h3>
        <p className="text-sm">
          Get concise overviews and key takeaways extracted by AI.
        </p>
      </div>
    ),
    placement: 'top',
  },

  {
    target: '[data-tour="video-actions-tab"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Action Items & MCQs</h3>
        <p className="text-sm">
          View extracted action items and automatically generated questions.
        </p>
      </div>
    ),
    placement: 'top',
  },

  //
  // DATAFRAMES — Deduplicated
  //

  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Dataframes Tool</h3>
        <p className="text-sm">
          Work with structured data using spreadsheet-like tools.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="df-undo"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Undo Changes</h3>
        <p className="text-sm">
          Use Ctrl+Z or this button to revert your last action.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="df-redo"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Redo Changes</h3>
        <p className="text-sm">
          Redo actions you have undone. Use Ctrl+Y for quick access.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="df-history"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Action History</h3>
        <p className="text-sm">
          View a timeline of your edits and jump to any previous version.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="df-new-sheet"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Create New Sheet</h3>
        <p className="text-sm">
          Add sheets or start from templates to organize your data.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="df-open-workbooks"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Open Workbooks</h3>
        <p className="text-sm">
          Access your saved spreadsheets or create new ones.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="df-import"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Import Data</h3>
        <p className="text-sm">
          Import Excel or CSV files to begin working with your data.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="df-export"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Export Workbook</h3>
        <p className="text-sm">
          Export your workbook as an Excel file for sharing or backup.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  //
  // SPEED READER — Deduplicated
  //

  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Speed Reader Tool</h3>
        <p className="text-sm">
          Analyze documents quickly and extract key insights.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="speed-reader-text-input"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Text Input Area</h3>
        <p className="text-sm">
          Paste text here to speed-read it in chunks.
        </p>
      </div>
    ),
    placement: 'right',
  },

  {
    target: '[data-tour="speed-reader-summary-button"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Generate Summary</h3>
        <p className="text-sm">
          Produce an AI-generated summary of the text.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="speed-reader-mcq-button"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Generate Questions</h3>
        <p className="text-sm">
          Create multiple-choice questions based on the text.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="speed-reader-sessions"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Manage Reading Sessions</h3>
        <p className="text-sm">
          Save, load, and manage texts across multiple reading sessions.
        </p>
      </div>
    ),
    placement: 'left',
  },

  //
  // MAP MAKER — Deduplicated
  //

  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Map Maker Tool</h3>
        <p className="text-sm">
          Create visual maps and diagrams with AI assistance.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="map-enhance-prompt"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Enhance Your Prompt</h3>
        <p className="text-sm">
          Improve your diagram description using AI.
        </p>
      </div>
    ),
    placement: 'top',
  },

  {
    target: '[data-tour="map-generate-diagram"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Generate Diagram</h3>
        <p className="text-sm">
          Generate a Mermaid diagram from your prompt.
        </p>
      </div>
    ),
    placement: 'top',
  },

  {
    target: '[data-tour="map-diagram-tab"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Diagram View</h3>
        <p className="text-sm">
          View your generated diagram with pan and zoom controls.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="map-flowmap-tab"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Interactive Flow Map</h3>
        <p className="text-sm">
          Edit nodes and connections in your diagram structure.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="map-download-svg"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Download Diagram</h3>
        <p className="text-sm">
          Download your diagram as an SVG for presentations or documentation.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="map-send-blueprint"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Send to Blueprint</h3>
        <p className="text-sm">
          Convert your map into a blueprint for deeper planning.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  //
  // BLUEPRINT TOOL — Deduplicated
  //

  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Blueprint Tool</h3>
        <p className="text-sm">
          Generate detailed project blueprints with phases and milestones.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="blueprint-objective-input"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Project Objective</h3>
        <p className="text-sm">
          Describe your objectives and expected outcomes.
        </p>
      </div>
    ),
    placement: 'right',
  },

  {
    target: '[data-tour="blueprint-attach-files"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Attach Supporting Files</h3>
        <p className="text-sm">
          Upload documents or research papers to guide blueprint generation.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="blueprint-generate"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Generate Blueprint</h3>
        <p className="text-sm">
          Create a full project plan with phases and milestones.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="blueprint-right-panel"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Saved Blueprints</h3>
        <p className="text-sm">
          Access all previously generated blueprints here.
        </p>
      </div>
    ),
    placement: 'left',
  },

  //
  // AI TOOLCHAIN — Deduplicated
  //

  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">AI Toolchain Creator</h3>
        <p className="text-sm">
          Build custom AI workflows by chaining tools together.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="toolchain-search"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Search Research Tools</h3>
        <p className="text-sm">
          Search through available tools for specific functions.
        </p>
      </div>
    ),
    placement: 'top',
  },

  {
    target: '[data-tour="toolchain-templates"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Load Saved Templates</h3>
        <p className="text-sm">
          Load pre-built toolchains for common research workflows.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="toolchain-clear"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Clear Current Chain</h3>
        <p className="text-sm">
          Remove all tools to start a new workflow.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="toolchain-builder"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Chain Builder Workspace</h3>
        <p className="text-sm">
          Drag and drop tools to construct your automated chain.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="toolchain-save"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Save Your Chain</h3>
        <p className="text-sm">
          Save the workflow for future use or sharing.
        </p>
      </div>
    ),
    placement: 'left',
  },

  {
    target: '[data-tour="toolchain-run"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Execute Research Chain</h3>
        <p className="text-sm">
          Run your workflow to process data through each tool.
        </p>
      </div>
    ),
    placement: 'left',
  },

  //
  // AI ADVISOR — Deduplicated
  //

  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">AI Advisor</h3>
        <p className="text-sm">
          Get expert recommendations and best practices for your project.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="advisor-tools-panel"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">AI Writing Tools</h3>
        <p className="text-sm">
          Choose from multiple text-processing tools at the top panel.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="advisor-language-select"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Translation Language</h3>
        <p className="text-sm">
          Select the target language for translation tasks.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="advisor-input-text"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Input Your Text</h3>
        <p className="text-sm">
          Paste or type the text you want processed by the AI.
        </p>
      </div>
    ),
    placement: 'right',
  },

  {
    target: '[data-tour="advisor-attach-files"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Attach Documents</h3>
        <p className="text-sm">
          Upload docs so AI can use them as context for processing.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="advisor-process-text"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Process Your Text</h3>
        <p className="text-sm">
          Click to run the selected tool on your input. Click again to cancel.
        </p>
      </div>
    ),
    placement: 'top',
  },

  {
    target: '[data-tour="advisor-output-text"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Processed Results</h3>
        <p className="text-sm">
          View the AI-generated results after processing your text.
        </p>
      </div>
    ),
    placement: 'left',
  },

  //
  // CLIENT MANAGEMENT — Deduplicated
  //

  {
    target: '[data-tour="clients-section"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Client Scheduling</h3>
        <p className="text-sm">
          Schedule meetings and coordinate client-related activities.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="clients-section"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Client Directory</h3>
        <p className="text-sm">
          Your complete client database. Add new clients, edit information,
          assign projects, and track all client relationships.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="clients-import-button"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Import Clients</h3>
        <p className="text-sm">
          Bulk import clients from Excel or CSV files. Automatically parses contact information
          and adds multiple clients at once.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="clients-export-button"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Export Clients</h3>
        <p className="text-sm">
          Export the client directory to CSV or Excel format.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  {
    target: '[data-tour="clients-add-client-button"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Add New Client</h3>
        <p className="text-sm">
          Manually add a new client to your database.
        </p>
      </div>
    ),
    placement: 'bottom',
  },

  //
  // NOTES
  //

  {
    target: '[data-tour="notes-section"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Knowledge Base</h3>
        <p className="text-sm">
          Create and organize your notes, documentation, and insights.
        </p>
      </div>
    ),
    placement: 'center',
  },

  //
  // PROFILE SETTINGS (General, Security, Notification, Onboarding)
  //

  {
    target: '[data-tour="profile-settings"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Profile Information</h3>
        <p className="text-sm">
          Update your personal info like name, title, and contact details.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="profile-settings"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Account Security</h3>
        <p className="text-sm">
          Change your password and manage security-related options.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="profile-settings"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Notification Preferences</h3>
        <p className="text-sm">
          Control what notifications you receive and how they appear.
        </p>
      </div>
    ),
    placement: 'center',
  },

  {
    target: '[data-tour="profile-settings"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Onboarding & Tutorials</h3>
        <p className="text-sm">
          Restart the guided tour or reset onboarding progress.
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          You're all set! Use the wheel menu (press 'G') for quick actions anywhere in the app.
        </p>
      </div>
    ),
    placement: 'center',
  },
];


// Consultant Tour Steps
export const getConsultantTourSteps = (): Step[] => [
  // Consultant: Welcome step
  {
    target: '[data-tour="dashboard-welcome"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold text-lg">Welcome to Synkro!</h3>
        <p className="text-sm">
          Let's explore your consultant workspace. This dashboard shows your active projects,
          recent activities, and quick access to everything you need.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="header-sidebar"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Navigation Sidebar</h3>
        <p className="text-sm">
          This is your main navigation sidebar. Use it to quickly access your dashboard,
          projects, calendar, research tools, and clients. Hover over it to see full labels.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '[data-tour="header-project-selector"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Project Selector</h3>
        <p className="text-sm">
          Switch between your active projects here. Click to see all available projects
          and select the one you want to work on. The current project is highlighted.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="header-notifications"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Notifications</h3>
        <p className="text-sm">
          Stay updated with project activities, mentions, deadlines, and important updates.
          The badge shows unread notifications. Click to view and manage all notifications.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="header-messages"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Messages</h3>
        <p className="text-sm">
          Communicate with your team members and clients. View your recent conversations
          and direct messages. The badge indicates unread messages.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="header-notes-icon"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Notes</h3>
        <p className="text-sm">
          Access your personal notes and knowledge base. Click here to view, create, and organize
          your notes. Keep important information at your fingertips.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="header-pomodoro-timer"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Pomodoro Timer</h3>
        <p className="text-sm">
          Use the Pomodoro technique to manage your work time. Click to start a focused work session,
          take breaks, and boost your productivity. The timer helps you maintain focus and rest cycles.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="header-three-dots-menu"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Quick Actions Menu</h3>
        <p className="text-sm">
          Access quick actions like creating events, tasks, and notes. You can also open
          the AI Assistant from here. This menu provides shortcuts to common actions.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="header-avatar"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Your Profile</h3>
        <p className="text-sm">
          Click on your avatar to access your profile settings, theme preferences, contacts,
          usage statistics, and account options. Manage your account and preferences here.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="projects-section"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Your Projects</h3>
        <p className="text-sm">
          View and manage all your assigned projects. Each project has its own workspace
          with files, chat, and progress tracking.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="upcoming-items"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Upcoming Deadlines</h3>
        <p className="text-sm">
          Stay on top of important dates and deadlines across all your projects.
          Never miss a milestone or meeting again.
        </p>
      </div>
    ),
    placement: 'left',
  },
  {
    target: '[data-tour="project-tasks"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Project Objectives</h3>
        <p className="text-sm">
          Track key objectives and goals for your current project.
          See progress and stay focused on what matters most.
        </p>
      </div>
    ),
    placement: 'left',
  },
  {
    target: '[data-tour="recent-activity"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Recent Activity</h3>
        <p className="text-sm">
          Monitor what's happening across your projects. See updates from team members
          and track the latest developments.
        </p>
      </div>
    ),
    placement: 'left',
  },
  {
    target: '[data-tour="project-team"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Project Team</h3>
        <p className="text-sm">
          See who's working on your current project. Contact team members
          directly and coordinate effectively.
        </p>
      </div>
    ),
    placement: 'right',
  },
  // Consultant: Assigned clients view
  {
    target: '[data-tour="assigned-clients"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Assigned Clients</h3>
        <p className="text-sm">
          View clients associated with your current project.
          Manage relationships and track client interactions.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '[data-tour="create-project"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Projects Overview</h3>
        <p className="text-sm">
          This is your projects page where you can see all your work.
          Create new projects, manage existing ones, and track progress.
        </p>
      </div>
    ),
    placement: 'center',
  },
  {
    target: '[data-tour="create-project-button"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Start New Projects</h3>
        <p className="text-sm">
          Click this button to create a new project.
          Choose from templates or create custom workflows.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="calendar-section"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Calendar & Deadlines</h3>
        <p className="text-sm">
          Keep track of meetings, deadlines, and important dates.
          Sync with your external calendars for better coordination.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // Research Tools - Individual steps for each tool
  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Conversational AI Assistant</h3>
        <p className="text-sm">
          Your AI-powered research assistant that can analyze documents,
          answer questions, and provide insights about your projects.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // Conversational AI Assistant - Menu Toggle
  {
    target: '[data-tour="ai-chat-menu-toggle"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Chat History Menu</h3>
        <p className="text-sm">
          Toggle the chat history sidebar to view and manage all your conversations.
          Access previous chats and start new ones.
        </p>
      </div>
    ),
    placement: 'right',
  },
  // Conversational AI Assistant - New Chat
  {
    target: '[data-tour="ai-chat-new-session"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Start New Chat</h3>
        <p className="text-sm">
          Create a new conversation session. Each chat maintains its own context
          and history for focused discussions.
        </p>
      </div>
    ),
    placement: 'right',
  },
  // Conversational AI Assistant - File Panel
  {
    target: '[data-tour="ai-chat-file-panel"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Research Files</h3>
        <p className="text-sm">
          Manage uploaded files and documents. The AI can analyze these files
          and use them as context for more accurate responses.
        </p>
      </div>
    ),
    placement: 'left',
  },
  // Conversational AI Assistant - Message Input
  {
    target: '[data-tour="ai-chat-message-input"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Message Input</h3>
        <p className="text-sm">
          Type your questions and requests here. Press Enter to send, or Shift+Enter for new lines.
          The AI will respond based on your uploaded files and conversation history.
        </p>
      </div>
    ),
    placement: 'top',
  },
  // Conversational AI Assistant - Attach Files
  {
    target: '[data-tour="ai-chat-attach-files"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Attach Files</h3>
        <p className="text-sm">
          Upload documents, PDFs, images, and spreadsheets to provide context
          for your AI conversations. Supported formats include PDF, DOCX, TXT, images, and Excel files.
        </p>
      </div>
    ),
    placement: 'top',
  },
  // Conversational AI Assistant - Voice Input
  {
    target: '[data-tour="ai-chat-voice-input"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Voice Input</h3>
        <p className="text-sm">
          Use voice input to communicate with the AI assistant. Click to start recording
          and speak your questions or requests.
        </p>
      </div>
    ),
    placement: 'top',
  },
  // Conversational AI Assistant - Send Message
  {
    target: '[data-tour="ai-chat-send-message"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Send Message</h3>
        <p className="text-sm">
          Send your message to the AI assistant. The send button turns red during
          streaming responses - click it to stop generation if needed.
        </p>
      </div>
    ),
    placement: 'top',
  },
  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Video Insights Tool</h3>
        <p className="text-sm">
          Extract key information from videos and multimedia content.
          Upload videos or provide URLs to get AI-powered analysis.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // Video Insights Tool - Browse Button
  {
    target: '[data-tour="video-browse"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Browse & Upload Videos</h3>
        <p className="text-sm">
          Upload videos from your device, select from project files, or import from meeting recordings.
          Supports various video formats and meeting platforms like Zoom, Teams, and Meet.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // Video Insights Tool - Saved Insights
  {
    target: '[data-tour="video-saved-insights"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Saved Video Insights</h3>
        <p className="text-sm">
          Access all your previously analyzed videos. View transcripts, summaries, and generated content.
          Manage and organize your video library with status tracking.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // Video Insights Tool - URL Input
  {
    target: '[data-tour="video-url-input"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Video URL Input</h3>
        <p className="text-sm">
          Paste video URLs from YouTube, Vimeo, or other platforms to analyze remote videos.
          The tool will automatically process and transcribe the content.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // Video Insights Tool - Transcript Tab
  {
    target: '[data-tour="video-transcript-tab"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Video Transcript</h3>
        <p className="text-sm">
          View the full transcript of your video with timestamps. Click on any word to jump
          to that exact moment in the video playback.
        </p>
      </div>
    ),
    placement: 'top',
  },
  // Video Insights Tool - Minutes Tab
  {
    target: '[data-tour="video-minutes-tab"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Meeting Minutes</h3>
        <p className="text-sm">
          Automatically generated meeting minutes and key points from the video transcript.
          Structured notes that capture the main discussion points and decisions.
        </p>
      </div>
    ),
    placement: 'top',
  },
  // Video Insights Tool - Summary Tab
  {
    target: '[data-tour="video-summary-tab"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">AI Summary</h3>
        <p className="text-sm">
          AI-generated summary of the video content. Get concise overviews and key takeaways
          that highlight the most important information from your videos.
        </p>
      </div>
    ),
    placement: 'top',
  },
  // Video Insights Tool - Actions Tab
  {
    target: '[data-tour="video-actions-tab"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Action Items & MCQs</h3>
        <p className="text-sm">
          View extracted action items, tasks, and multiple-choice questions generated from
          the video content. Perfect for training materials and follow-up activities.
        </p>
      </div>
    ),
    placement: 'top',
  },
  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Dataframes Tool</h3>
        <p className="text-sm">
          Work with structured data using powerful dataframe tools.
          Import, analyze, and visualize data to gain insights.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // Dataframes Tool - Undo
  {
    target: '[data-tour="df-undo"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Undo Changes</h3>
        <p className="text-sm">
          Undo your last action. Use Ctrl+Z to quickly undo changes
          and revert to previous states of your spreadsheet.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // Dataframes Tool - Redo
  {
    target: '[data-tour="df-redo"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Redo Changes</h3>
        <p className="text-sm">
          Redo actions that were previously undone. Use Ctrl+Y to quickly
          restore changes you may have accidentally undone.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // Dataframes Tool - History
  {
    target: '[data-tour="df-history"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Action History</h3>
        <p className="text-sm">
          View and jump to previous states of your spreadsheet.
          Access a timeline of all your recent changes and modifications.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // Dataframes Tool - New Sheet
  {
    target: '[data-tour="df-new-sheet"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Create New Sheet</h3>
        <p className="text-sm">
          Add new sheets to your workbook or start with a template.
          Organize your data across multiple tabs for better structure.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // Dataframes Tool - Open Workbooks
  {
    target: '[data-tour="df-open-workbooks"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Open Workbooks</h3>
        <p className="text-sm">
          Access your saved workbooks and create new ones.
          Manage all your spreadsheet projects in one place.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // Dataframes Tool - Import
  {
    target: '[data-tour="df-import"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Import Data</h3>
        <p className="text-sm">
          Import Excel files (.xlsx, .xls) and CSV files to get started quickly.
          Bring in existing data from other sources and applications.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // Dataframes Tool - Export
  {
    target: '[data-tour="df-export"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Export Workbook</h3>
        <p className="text-sm">
          Export your workbook as an Excel file (.xlsx) for sharing
          or use in other applications. Preserve all your formatting and data.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Speed Reader Tool</h3>
        <p className="text-sm">
          Read and analyze documents at high speed with AI assistance.
          Extract key information and insights from lengthy documents quickly.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // Speed Reader Tool - Text Input
  {
    target: '[data-tour="speed-reader-text-input"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Text Input Area</h3>
        <p className="text-sm">
          Paste or type the text you want to speed read here. The tool will break it down
          into chunks and display them at your chosen reading speed.
        </p>
      </div>
    ),
    placement: 'right',
  },
  // Speed Reader Tool - Summary Button
  {
    target: '[data-tour="speed-reader-summary-button"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Generate Summary</h3>
        <p className="text-sm">
          Create an AI-generated summary of the text. Get the key points
          and main ideas without reading the entire document.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // Speed Reader Tool - MCQ Button
  {
    target: '[data-tour="speed-reader-mcq-button"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Generate Questions</h3>
        <p className="text-sm">
          Create multiple choice questions to test your understanding
          and reinforce learning from the text.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // Speed Reader Tool - Sessions
  {
    target: '[data-tour="speed-reader-sessions"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Manage Reading Sessions</h3>
        <p className="text-sm">
          Save and manage your reading sessions. Access previous texts, track progress,
          and switch between different reading materials.
        </p>
      </div>
    ),
    placement: 'left',
  },
  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Map Maker Tool</h3>
        <p className="text-sm">
          Create visual maps and diagrams to model complex scenarios and processes.
          Organize ideas and relationships with interactive mapping tools.
        </p>
      </div>
    ),
    placement: 'center',
  },
  {
    target: '[data-tour="map-enhance-prompt"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Enhance Your Prompt</h3>
        <p className="text-sm">
          Use AI to refine and improve your diagram description for better results.
        </p>
      </div>
    ),
    placement: 'top',
  },
  {
    target: '[data-tour="map-generate-diagram"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Generate Diagram</h3>
        <p className="text-sm">
          Click here to generate your Mermaid diagram based on the description you provided.
        </p>
      </div>
    ),
    placement: 'top',
  },
  {
    target: '[data-tour="map-diagram-tab"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Diagram View</h3>
        <p className="text-sm">
          View your generated diagram in visual form. You can pan, zoom, and interact with the diagram.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="map-flowmap-tab"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Interactive Flow Map</h3>
        <p className="text-sm">
          Switch to the interactive flow map view to edit nodes, add connections, and modify your diagram structure.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="map-download-svg"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Download Diagram</h3>
        <p className="text-sm">
          Download your diagram as an SVG file for use in presentations or documents.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="map-send-blueprint"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Send to Blueprint</h3>
        <p className="text-sm">
          Convert your diagram to a Blueprint for detailed project planning and task management.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Blueprint Tool</h3>
        <p className="text-sm">
          Design and prototype project blueprints with AI assistance.
          Create detailed project plans and workflow diagrams.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // Blueprint Tool - Objective Input
  {
    target: '[data-tour="blueprint-objective-input"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Project Objective</h3>
        <p className="text-sm">
          Describe your research objective, goals, and expected outcomes.
          The AI will use this to generate a comprehensive project blueprint.
        </p>
      </div>
    ),
    placement: 'right',
  },
  // Blueprint Tool - Attach Files
  {
    target: '[data-tour="blueprint-attach-files"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Attach Supporting Files</h3>
        <p className="text-sm">
          Upload documents, research papers, or existing project plans.
          The AI will analyze these files to create more accurate blueprints.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // Blueprint Tool - Generate
  {
    target: '[data-tour="blueprint-generate"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Generate Blueprint</h3>
        <p className="text-sm">
          Click to generate a comprehensive project blueprint with phases,
          tasks, milestones, and risk assessments based on your objective.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // Blueprint Tool - Right Panel with Saved Blueprints
  {
    target: '[data-tour="blueprint-right-panel"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Saved Blueprints</h3>
        <p className="text-sm">
          Your generated blueprints appear here. Click on any blueprint to view details,
          see the timeline, tasks, milestones, risks, and manage your project plans.
        </p>
      </div>
    ),
    placement: 'left',
  },
  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">AI Toolchain Creator</h3>
        <p className="text-sm">
          Build custom AI workflows and automation chains for your projects.
          Combine multiple AI tools into powerful automated processes.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // AI Toolchain Creator - Search
  {
    target: '[data-tour="toolchain-search"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Search Research Tools</h3>
        <p className="text-sm">
          Search through available research tools by name or functionality.
          Find specific tools like market analysis, competitor research, or data visualization.
        </p>
      </div>
    ),
    placement: 'top',
  },
  // AI Toolchain Creator - Templates
  {
    target: '[data-tour="toolchain-templates"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Load Saved Templates</h3>
        <p className="text-sm">
          Access your saved research chains and templates. Load pre-built workflows
          for common research scenarios like market analysis or competitive intelligence.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // AI Toolchain Creator - Clear
  {
    target: '[data-tour="toolchain-clear"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Clear Current Chain</h3>
        <p className="text-sm">
          Remove all tools from your current research chain. Start fresh when you want
          to build a completely new workflow from scratch.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // AI Toolchain Creator - Chain Builder
  {
    target: '[data-tour="toolchain-builder"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Chain Builder Workspace</h3>
        <p className="text-sm">
          Drag and drop research tools to build your automated workflow. Configure each tool's
          parameters and define how data flows between different research steps.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // AI Toolchain Creator - Save
  {
    target: '[data-tour="toolchain-save"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Save Your Chain</h3>
        <p className="text-sm">
          Save your research chain as a template for future use. Give it a descriptive name
          so you can easily identify and reuse successful research workflows.
        </p>
      </div>
    ),
    placement: 'left',
  },
  // AI Toolchain Creator - Run
  {
    target: '[data-tour="toolchain-run"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Execute Research Chain</h3>
        <p className="text-sm">
          Run your complete research workflow. The system will execute each tool in sequence,
          passing data between tools and generating comprehensive research results.
        </p>
      </div>
    ),
    placement: 'left',
  },
  {
    target: '[data-tour="research-tools"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">AI Advisor</h3>
        <p className="text-sm">
          Get expert guidance and recommendations for your projects.
          Receive strategic advice and best practices from AI specialists.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // AI Advisor - Tools Panel
  {
    target: '[data-tour="advisor-tools-panel"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">AI Writing Tools</h3>
        <p className="text-sm">
          Choose from various AI-powered text processing tools at the top. Each tool specializes
          in different aspects of text improvement and analysis. Select a tool to get started.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // AI Advisor - Language Selection
  {
    target: '[data-tour="advisor-language-select"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Translation Language</h3>
        <p className="text-sm">
          Select the target language for translation. Choose from multiple languages
          including Spanish, French, German, and many more.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // AI Advisor - Input Text
  {
    target: '[data-tour="advisor-input-text"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Input Your Text</h3>
        <p className="text-sm">
          Enter or paste the text you want to process. The AI will analyze and
          transform your text according to the selected tool and settings.
        </p>
      </div>
    ),
    placement: 'right',
  },
  // AI Advisor - Attach Files
  {
    target: '[data-tour="advisor-attach-files"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Attach Documents</h3>
        <p className="text-sm">
          Upload files to include in your text processing. The AI can analyze
          PDF, Word documents, and other text files as context.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  // AI Advisor - Process Text
  {
    target: '[data-tour="advisor-process-text"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Process Your Text</h3>
        <p className="text-sm">
          Click to apply the selected AI tool to your text. The button turns red
          during processing - click it again to cancel if needed.
        </p>
      </div>
    ),
    placement: 'top',
  },
  // AI Advisor - Output Text
  {
    target: '[data-tour="advisor-output-text"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Processed Results</h3>
        <p className="text-sm">
          View the AI-processed output here. The results will vary based on
          the selected tool and your input text.
        </p>
      </div>
    ),
    placement: 'left',
  },
  // Client Management - Individual steps for each tab
  {
    target: '[data-tour="clients-section"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Client Scheduling</h3>
        <p className="text-sm">
          Schedule meetings with clients, track file requests, and coordinate
          project activities efficiently.
        </p>
      </div>
    ),
    placement: 'center',
  },
  {
    target: '[data-tour="clients-section"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Client Directory</h3>
        <p className="text-sm">
          Your complete client database. Add new clients, edit information,
          assign projects, and track all client relationships.
        </p>
      </div>
    ),
    placement: 'center',
  },
  {
    target: '[data-tour="clients-import-button"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Import Clients</h3>
        <p className="text-sm">
          Bulk import clients from Excel or CSV files. Automatically parses contact information
          and adds multiple clients at once.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="clients-export-button"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Export Clients</h3>
        <p className="text-sm">
          Export your client database to CSV or Excel format for backup,
          reporting, or external use.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="clients-add-client-button"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Add New Client</h3>
        <p className="text-sm">
          Manually add a new client to your database. Fill in contact details,
          assign projects, and set visibility permissions.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="notes-section"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Knowledge Base</h3>
        <p className="text-sm">
          Create and organize notes, documentation, and insights.
          Build a personal knowledge base for your work.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // Profile - General tab
  {
    target: '[data-tour="profile-settings"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Profile Information</h3>
        <p className="text-sm">
          Update your personal information, job title, and bio.
          This helps your team and clients know who you are.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // Profile - Security tab
  {
    target: '[data-tour="profile-settings"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Account Security</h3>
        <p className="text-sm">
          Change your password and manage your account security settings.
          Keep your account safe and secure.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // Profile - Notifications tab
  {
    target: '[data-tour="profile-settings"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Notification Preferences</h3>
        <p className="text-sm">
          Configure how and when you receive notifications. Customize
          your communication preferences for projects and messages.
        </p>
      </div>
    ),
    placement: 'center',
  },
  // Profile - Onboarding tab
  {
    target: '[data-tour="profile-settings"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Onboarding & Tutorials</h3>
        <p className="text-sm">
          Restart the product tour or reset your onboarding progress.
          Get guided help whenever you need it.
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          You're ready to start working! Press 'G' anytime for the quick action menu.
        </p>
      </div>
    ),
    placement: 'center',
  },
];

export const getClientTourSteps = (): Step[] => [
  {
    target: '[data-tour="dashboard-welcome"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold text-lg">Welcome to Your Portal!</h3>
        <p className="text-sm">
          Welcome to Synkro! This is your client dashboard where you can track
          project progress, communicate with your team, and access all deliverables.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="project-identity"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Project Overview</h3>
        <p className="text-sm">
          Get an overview of your project details, objectives, and current status.
          Stay informed about your project's progress and goals.
        </p>
      </div>
    ),
    placement: 'left',
  },
  {
    target: '[data-tour="project-team"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Your Project Team</h3>
        <p className="text-sm">
          Meet the team members working on your project. Contact them directly
          for questions or updates.
        </p>
      </div>
    ),
    placement: 'left',
  },
  {
    target: '[data-tour="mission-statement"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Project Mission</h3>
        <p className="text-sm">
          Understand the project's mission and objectives. This helps you see
          how your project fits into the bigger picture.
        </p>
      </div>
    ),
    placement: 'center',
  },
  {
    target: '[data-tour="project-files"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Project Documents</h3>
        <p className="text-sm">
          Access all project-related documents, reports, and deliverables
          in one secure location.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '[data-tour="project-activity"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Project Updates</h3>
        <p className="text-sm">
          Stay updated with the latest project activities and progress.
          See what's been accomplished recently.
        </p>
      </div>
    ),
    placement: 'right',
  },
  {
    target: '[data-tour="calendar-section"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Project Timeline</h3>
        <p className="text-sm">
          View important dates, milestones, and deadlines for your projects.
          Stay informed about upcoming deliverables and meetings.
        </p>
      </div>
    ),
    placement: 'center',
  },
  {
    target: '[data-tour="billing-section"]',
    content: (
      <div className="space-y-2">
        <h3 className="font-semibold">Billing & Invoices</h3>
        <p className="text-sm">
          View invoices, track payments, and monitor your project budget.
          Download receipts and payment confirmations.
        </p>
      </div>
    ),
    placement: 'center',
  },
];


// Helper function to extract title from step content
const extractTitleFromStep = (step: Step): string => {
  try {
    const element = step.content as any;
    if (element?.props?.children?.[0]?.props?.children) {
      return element.props.children[0].props.children;
    }
    if (element?.props?.children?.props?.children) {
      return element.props.children.props.children;
    }
  } catch (error) {
    console.warn('Could not extract title from step:', error);
  }
  return 'Untitled Step';
};

export const getTourSteps = (role: string | null): Step[] => {
  switch (role) {
    case 'manager':
      return getManagerTourSteps();
    case 'consultant':
    case 'admin':
      return getConsultantTourSteps();
    case 'client':
      return getClientTourSteps();
    case 'business':
      return getBusinessTourSteps();
    default:
      return [];
  }
};

export const getTourStepTitles = (role: string | null): string[] => {
  const steps = getTourSteps(role);
  return steps.map(step => extractTitleFromStep(step));
};

